import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  ArrowLeft, ArrowRight, CheckCircle, Clock, Dumbbell,
  X, Play, Pause, Plus, Trash2
} from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useWorkout } from "@/contexts/WorkoutContext";
import { SetDetail, Exercise } from "@/data/dummyData";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import {
  deleteTrackedSet,
  ensureTrackedSets,
  fetchAssignedWorkoutFull,
  fetchTrackedSets,
  updateAssignedExerciseFields,
  updateAssignedGroupingSets,
  updateTrackedSet,
  type TrackedSet,
} from "@/integrations/backend/workout-assigned";
import { generateExerciseImageUrl, getVideoEmbedUrl, getVideoThumbnailUrl } from "@/lib/exercise-media";
import { buildSessionSteps, type WorkoutSessionStep } from "@/lib/workout-session-steps";



function withAutoplay(embedUrl: string): string {
  try {
    const u = new URL(embedUrl);
    // YouTube
    if (u.hostname.includes("youtube.com") || u.hostname.includes("youtu.be")) {
      u.searchParams.set("autoplay", "1");
      u.searchParams.set("mute", "1");
      u.searchParams.set("playsinline", "1");
      return u.toString();
    }
    // Vimeo
    if (u.hostname.includes("vimeo.com") && u.pathname.includes("/video/")) {
      u.searchParams.set("autoplay", "1");
      u.searchParams.set("muted", "1");
      u.searchParams.set("playsinline", "1");
      return u.toString();
    }
    u.searchParams.set("autoplay", "1");
    return u.toString();
  } catch {
    return embedUrl.includes("?") ? `${embedUrl}&autoplay=1` : `${embedUrl}?autoplay=1`;
  }
}

function formatRestTime(seconds: number | null | undefined): string {
  if (!seconds) return "";
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return minutes === 1 ? "1 min" : `${minutes} min`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds} sec`;
}

const ExerciseMedia = ({ exercise }: { exercise: Exercise }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [exercise.id]);

  const videoUrl = (exercise as Exercise & { videoUrl?: string | null }).videoUrl ?? null;
  const imageUrl = (exercise as Exercise & { imageUrl?: string | null }).imageUrl ?? null;
  const embedUrl = getVideoEmbedUrl(videoUrl);
  const canPlayVideo = Boolean(embedUrl);

  // Requirement:
  // - If there's a video: show the video's thumbnail (Vimeo → vumbnail)
  // - If there's no video but there is an image: show the image (no play button)
  const posterSrc =
    (canPlayVideo ? getVideoThumbnailUrl(videoUrl) : null) ||
    imageUrl ||
    exercise.videoThumbnail ||
    generateExerciseImageUrl(exercise.name);

  if (isVideoPlaying && embedUrl) {
    return (
      <div className="relative">
        <AspectRatio ratio={16 / 9} className="bg-dark-slate rounded-lg overflow-hidden">
          <iframe
            title={`${exercise.name} video`}
            src={withAutoplay(embedUrl)}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </AspectRatio>
      </div>
    );
  }

  return (
    <div className="relative">
      <AspectRatio ratio={16 / 9} className="bg-dark-slate rounded-lg overflow-hidden">
        <img
          src={posterSrc}
          alt={`${exercise.name} demonstration`}
          className="object-cover w-full h-full"
          loading="lazy"
        />
      </AspectRatio>

      {canPlayVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
          <button
            type="button"
            onClick={() => setIsVideoPlaying(true)}
            className="bg-primary rounded-full p-3 shadow-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50"
            aria-label={`Play video for ${exercise.name}`}
          >
            <Play className="h-6 w-6 text-black fill-black translate-x-[1px]" />
          </button>
        </div>
      )}
    </div>
  );
};

type SessionSetDetail = SetDetail & { trackedSetId?: string };

// Updated Helper component for the sets tracking
const SetTrackingRow = ({ 
  setData, 
  exerciseId, 
  exercise,
  trackedSetId,
  onValueChange,
  canDelete = false,
  onDelete,
  deleteDisabled = false,
  showRestAfter = false,
  restSeconds
}: { 
  setData: SessionSetDetail; 
  exerciseId: string;
  exercise: Exercise;
  trackedSetId: string | null;
  onValueChange: (exerciseId: string, setNumber: number, values: Partial<Pick<SetDetail, "weight" | "reps" | "duration">>) => void;
  canDelete?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  showRestAfter?: boolean;
  restSeconds?: number | null;
}) => {
  const [weight, setWeight] = useState(setData.weight ?? 0);
  const [reps, setReps] = useState(setData.reps ?? 0);
  const [duration, setDuration] = useState(setData.duration ?? 0);
  
  // Determine which fields to show based on exercise data
  // Check metadata flags if available, otherwise fall back to checking string values
  const exerciseWithMeta = exercise as Exercise & { _hasReps?: boolean; _hasWeight?: boolean; _hasTime?: boolean };
  const showWeight = exerciseWithMeta._hasWeight ?? (exercise.weight != null && exercise.weight !== "");
  const showReps = exerciseWithMeta._hasReps ?? (exercise.reps != null && exercise.reps !== "");
  const showDuration = exerciseWithMeta._hasTime ?? false;
  
  const handleWeightChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setWeight(newValue);
    onValueChange(exerciseId, setData.set, { weight: newValue, reps });
    // Auto-save if trackedSetId exists
    if (trackedSetId) {
      try {
        await updateTrackedSet(trackedSetId, { weight_lbs: newValue || null });
      } catch (error) {
        console.error("Failed to save weight:", error);
      }
    }
  };
  
  const handleRepsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setReps(newValue);
    onValueChange(exerciseId, setData.set, { weight, reps: newValue });
    // Auto-save if trackedSetId exists
    if (trackedSetId) {
      try {
        await updateTrackedSet(trackedSetId, { reps: newValue || null });
      } catch (error) {
        console.error("Failed to save reps:", error);
      }
    }
  };
  
  const handleDurationChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setDuration(newValue);
    onValueChange(exerciseId, setData.set, { duration: newValue });
    // Auto-save if trackedSetId exists
    if (trackedSetId) {
      try {
        await updateTrackedSet(trackedSetId, { time_seconds: newValue || null });
      } catch (error) {
        console.error("Failed to save duration:", error);
      }
    }
  };
  
  // Determine grid columns based on visible fields
  const visibleFields = [showWeight, showReps, showDuration].filter(Boolean).length;
  const gridCols = visibleFields === 1 ? "grid-cols-1" : visibleFields === 2 ? "grid-cols-2" : "grid-cols-3";
  
  const formatRestTime = (seconds: number | null | undefined): string => {
    if (!seconds) return "";
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return minutes === 1 ? "1 min" : `${minutes} min`;
    }
    return `${seconds} sec`;
  };
  
  return (
    <>
      <div className="grid gap-2 rounded-lg border border-white/10 bg-dark-slate p-3">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-primary">Set {setData.set}</span>
          {canDelete && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onDelete}
              disabled={deleteDisabled}
              aria-label="Delete last set"
              title="Delete last set"
              className="h-8 w-8 rounded-md text-white/80 hover:bg-white/10 hover:text-white"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className={cn("grid gap-2", gridCols)}>
          {showWeight && (
            <div>
              <label className="mb-1 block text-xs text-gray-light">Weight (lbs)</label>
              <Input 
                type="number"
                value={weight}
                onChange={handleWeightChange}
                className="h-9 border-white/10 bg-black/20 text-sm text-white placeholder:text-gray-light focus-visible:ring-primary/40"
              />
            </div>
          )}
          {showReps && (
            <div>
              <label className="mb-1 block text-xs text-gray-light">Reps</label>
              <Input 
                type="number"
                value={reps}
                onChange={handleRepsChange}
                className="h-9 border-white/10 bg-black/20 text-sm text-white placeholder:text-gray-light focus-visible:ring-primary/40"
              />
            </div>
          )}
          {showDuration && (
            <div>
              <label className="mb-1 block text-xs text-gray-light">Duration (sec)</label>
              <Input 
                type="number"
                value={duration}
                onChange={handleDurationChange}
                className="h-9 border-white/10 bg-black/20 text-sm text-white placeholder:text-gray-light focus-visible:ring-primary/40"
              />
            </div>
          )}
        </div>
      </div>
      {showRestAfter && restSeconds && (
        <div className="py-2 text-center text-sm text-gray-light">
          Rest {formatRestTime(restSeconds)}
        </div>
      )}
    </>
  );
};

const WorkoutSession = () => {
  useEnforceLegalApproval();
  const [searchParams] = useSearchParams();
  const { 
    isWorkoutActive,
    activeWorkout,
    activeAssignedWorkoutId,
    setActiveAssignedWorkoutId,
    isPaused,
    completedExercises,
    activeExerciseIndex,
    startWorkout,
    replaceActiveWorkout,
    pauseResumeWorkout,
    endWorkout,
    setActiveExerciseIndex
  } = useWorkout();
  
  // Updated set tracking state with proper typing
  const [exerciseSetData, setExerciseSetData] = useState<Record<string, SessionSetDetail[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutatingSets, setIsMutatingSets] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  
  // Timer state
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedTimeRef = useRef<number>(0);
  
  // Ref for progress bar to scroll to
  const progressBarRef = useRef<HTMLDivElement>(null);
  
  // Load timer from cookie on mount
  useEffect(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('workout_elapsed_time='))
      ?.split('=')[1];
    if (cookieValue) {
      const savedSeconds = parseInt(cookieValue, 10);
      if (!isNaN(savedSeconds)) {
        setElapsedSeconds(savedSeconds);
        pausedTimeRef.current = savedSeconds;
      }
    }
  }, []);

  // Timer effect - starts when workout becomes active, persists through all exercises
  useEffect(() => {
    if (!isWorkoutActive) {
      // Clear interval when workout is not active
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      startTimeRef.current = null;
      pausedTimeRef.current = 0;
      return;
    }

    // Initialize timer on first exercise - only reset if no saved time exists
    // This prevents resetting the timer when resuming a paused workout
    if (startTimeRef.current === null && activeExerciseIndex === 0 && pausedTimeRef.current === 0) {
      startTimeRef.current = Date.now();
      // pausedTimeRef.current is already 0, so no need to reset it
    }

    // Handle pause/resume
    if (isPaused) {
      // Pause: stop timer and save accumulated time
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      // Save accumulated time when pausing
      if (startTimeRef.current !== null) {
        const currentElapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        pausedTimeRef.current += currentElapsed;
        setElapsedSeconds(pausedTimeRef.current);
        startTimeRef.current = null; // Reset for next resume
      }
    } else {
      // Resume or continue: start/restart timer
      if (!timerIntervalRef.current) {
        // Start new segment
        startTimeRef.current = Date.now();
        
        timerIntervalRef.current = setInterval(() => {
          if (startTimeRef.current !== null) {
            const now = Date.now();
            const segmentElapsed = Math.floor((now - startTimeRef.current) / 1000);
            const totalElapsed = segmentElapsed + pausedTimeRef.current;
            setElapsedSeconds(totalElapsed);
            
            // Save to cookie
            document.cookie = `workout_elapsed_time=${totalElapsed}; path=/; max-age=86400`; // 24 hours
          }
        }, 1000);
      }
    }

    return () => {
      if (timerIntervalRef.current && isPaused) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isWorkoutActive, isPaused, activeExerciseIndex]);

  // Format timer display
  const formatTimer = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Set up the workout if not already active and initialize set data
  useEffect(() => {
    console.log("WorkoutSession useEffect - isWorkoutActive:", isWorkoutActive, "activeWorkout:", activeWorkout);
    
    if (!isWorkoutActive) {
      const assignedId = searchParams.get("assigned");
      if (!assignedId) {
        setError("No assigned workout ID found. Please select a workout to start.");
        return;
      }
      
      setActiveAssignedWorkoutId(assignedId);
      setIsLoading(true);
      setError(null);
      
      // Fetch full workout data
      (async () => {
        try {
          const workout = await fetchAssignedWorkoutFull(assignedId);
          
          startWorkout(workout);
          
          // Initialize set tracking data
          const initialSetData: Record<string, SessionSetDetail[]> = {};
          for (const exercise of workout.exercises) {
            if (exercise.setDetails && exercise.setDetails.length > 0) {
              initialSetData[exercise.id] = exercise.setDetails;
            } else {
              // Create default set details based on sets count
              initialSetData[exercise.id] = Array.from({ length: exercise.sets }, (_, i) => ({
                set: i + 1,
                weight: undefined,
                reps: undefined,
                duration: undefined
              }));
            }
          }
          setExerciseSetData(initialSetData);
          setIsLoading(false);
        } catch (e) {
          console.error("Failed to fetch workout:", e);
          setError("Failed to load workout. Please try again or contact support if the problem persists.");
          setIsLoading(false);
        }
      })();
    } else if (activeWorkout && Object.keys(exerciseSetData).length === 0) {
      // Initialize set tracking data from active workout when available
      console.log("Initializing set data for active workout");
      const initialSetData: Record<string, SessionSetDetail[]> = {};
      
      activeWorkout.exercises.forEach(exercise => {
        if (exercise.setDetails && exercise.setDetails.length > 0) {
          initialSetData[exercise.id] = exercise.setDetails;
        } else {
          // Create default set details if not provided
          initialSetData[exercise.id] = Array.from({ length: exercise.sets }, (_, i) => ({
            set: i + 1,
            weight: undefined,
            reps: undefined,
            duration: undefined,
            completed: false
          }));
        }
      });
      
      setExerciseSetData(initialSetData);
    }
  }, [isWorkoutActive, activeWorkout, exerciseSetData, startWorkout, searchParams, setActiveAssignedWorkoutId]);
  
  const exerciseGroups = activeWorkout?.exerciseGroups ?? activeWorkout?.exercises.map((exercise) => ({
    id: exercise.id,
    label: "Single" as const,
    rounds: exercise.sets,
    restSeconds: 0,
    exercises: [exercise],
  })) ?? [];
  const exerciseIndexById = useMemo(
    () => new Map((activeWorkout?.exercises ?? []).map((exercise, index) => [exercise.id, index])),
    [activeWorkout?.exercises]
  );
  const sessionSteps = useMemo<WorkoutSessionStep[]>(
    () => buildSessionSteps(exerciseGroups, exerciseIndexById),
    [exerciseGroups, exerciseIndexById]
  );

  useEffect(() => {
    setActiveStepIndex(0);
  }, [activeWorkout?.id]);

  const safeActiveStepIndex = sessionSteps.length > 0
    ? Math.min(activeStepIndex, sessionSteps.length - 1)
    : 0;
  const activeStep = sessionSteps[safeActiveStepIndex];
  const activeExercise = activeWorkout?.exercises[activeStep?.exerciseIndex ?? 0];
  const totalSteps = sessionSteps.length;
  const activeExerciseGroup = exerciseGroups[activeStep?.groupIndex ?? 0] ?? exerciseGroups[0];
  const activeExerciseGroupIndex = activeStep?.groupIndex ?? 0;
  const activeExerciseIndexInGroup = activeStep?.exerciseIndexInGroup ?? 0;
  const activeGrouping = activeExercise?.grouping;
  const equipmentNames = activeExercise?.equipment.filter(Boolean) ?? [];
  const equipmentDetails = activeExercise?.equipmentDetails ?? [];
  const hasExerciseInstructions = Boolean(activeExercise?.instructions.trim());
  const hasEquipment = equipmentNames.length > 0;
  const hasEquipmentInstructions = equipmentDetails.length > 0;
  const isGroupedExercise = Boolean(activeStep?.isGrouped && activeGrouping);
  const groupRounds = activeGrouping?.rounds ?? activeExercise?.sets ?? 0;
  const groupRestSeconds = activeGrouping?.restSeconds ?? 0;
  const isLastExerciseInGroup = isGroupedExercise && activeExerciseIndexInGroup === (activeGrouping?.size ?? 1) - 1;
  const showSetMutationControls = !isGroupedExercise;
  const currentStepNumber = safeActiveStepIndex + 1;
  const completedStepsCount = safeActiveStepIndex;
  const progress = totalSteps > 0 ? (completedStepsCount / totalSteps) * 100 : 0;

  useEffect(() => {
    if (activeStep && activeExerciseIndex !== activeStep.exerciseIndex) {
      setActiveExerciseIndex(activeStep.exerciseIndex);
    }
  }, [activeExerciseIndex, activeStep, setActiveExerciseIndex]);

  const stepIndexesByExerciseId = useMemo(() => {
    const map = new Map<string, number[]>();
    sessionSteps.forEach((step, index) => {
      const existing = map.get(step.exerciseId) ?? [];
      existing.push(index);
      map.set(step.exerciseId, existing);
    });
    return map;
  }, [sessionSteps]);

  const getStepIndexForExercise = (exerciseId: string, options?: { groupId?: string; roundIndex?: number }) => {
    const stepIndexes = stepIndexesByExerciseId.get(exerciseId) ?? [];
    if (stepIndexes.length === 0) return -1;

    const matchingIndexes = stepIndexes.filter((index) => {
      const step = sessionSteps[index];
      if (!step) return false;
      if (options?.groupId && step.groupId !== options.groupId) return false;
      if (options?.roundIndex != null && step.roundIndex !== options.roundIndex) return false;
      return true;
    });

    const candidateIndexes = matchingIndexes.length > 0 ? matchingIndexes : stepIndexes;
    return candidateIndexes.find((index) => index >= safeActiveStepIndex) ?? candidateIndexes[candidateIndexes.length - 1] ?? -1;
  };
  
  const handleSetValueChange = (
    exerciseId: string,
    setNumber: number,
    values: Partial<Pick<SetDetail, "weight" | "reps" | "duration">>
  ) => {
    setExerciseSetData(prev => {
      const updatedSetData = [...(prev[exerciseId] || [])];
      const setIndex = updatedSetData.findIndex(s => s.set === setNumber);
      
      if (setIndex !== -1) {
        updatedSetData[setIndex] = {
          ...updatedSetData[setIndex],
          ...values
        };
      }
      
      return {
        ...prev,
        [exerciseId]: updatedSetData
      };
    });
  };

  const trackedSetsToSetDetails = (trackedSets: TrackedSet[]): SessionSetDetail[] => {
    return trackedSets
      .slice()
      .sort((a, b) => a.set_number - b.set_number)
      .map((ts) => ({
        set: ts.set_number,
        weight: ts.weight_lbs ?? undefined,
        reps: ts.reps ?? undefined,
        duration: ts.time_seconds ?? undefined,
        trackedSetId: ts.id,
      }));
  };

  const refreshExerciseTrackedSets = async (exerciseId: string): Promise<SessionSetDetail[]> => {
    const trackedSets = await fetchTrackedSets(exerciseId);
    const setDetails = trackedSetsToSetDetails(trackedSets);
    setExerciseSetData((prev) => ({ ...prev, [exerciseId]: setDetails }));
    return setDetails;
  };

  const getEffectiveSetsCount = (exerciseId: string): number => {
    const fromState = exerciseSetData[exerciseId]?.length;
    return typeof fromState === "number" && fromState > 0 ? fromState : (activeWorkout?.exercises.find(e => e.id === exerciseId)?.sets ?? 0);
  };

  const handleAddSet = async (exerciseId: string) => {
    if (isMutatingSets) return;
    setIsMutatingSets(true);
    setError(null);
    try {
      const currentSets = getEffectiveSetsCount(exerciseId);
      await updateAssignedExerciseFields(exerciseId, { sets: currentSets + 1 });
      const trackedSets = await ensureTrackedSets(exerciseId);
      setExerciseSetData((prev) => ({ ...prev, [exerciseId]: trackedSetsToSetDetails(trackedSets) }));
    } catch (e) {
      console.error("Failed to add set:", e);
      setError("Failed to add a set. Please try again.");
    } finally {
      setIsMutatingSets(false);
    }
  };

  const handleAddGroupRound = async () => {
    if (isMutatingSets || !activeExerciseGroup || !activeStep?.groupId || !activeAssignedWorkoutId) return;
    setIsMutatingSets(true);
    setError(null);
    try {
      const groupId = activeStep.groupId;
      const currentRounds = activeExerciseGroup.rounds || activeGrouping?.rounds || 0;
      await updateAssignedGroupingSets(groupId, currentRounds + 1);

      await Promise.all(
        activeExerciseGroup.exercises.map((exercise) => ensureTrackedSets(exercise.id))
      );

      const refreshedWorkout = await fetchAssignedWorkoutFull(activeAssignedWorkoutId);
      replaceActiveWorkout(refreshedWorkout);

      setExerciseSetData((prev) => {
        const next = { ...prev };
        for (const exercise of activeExerciseGroup.exercises) {
          const refreshedExercise = refreshedWorkout.exercises.find((e) => e.id === exercise.id);
          if (refreshedExercise?.setDetails && refreshedExercise.setDetails.length > 0) {
            next[exercise.id] = refreshedExercise.setDetails;
          }
        }
        return next;
      });
    } catch (e) {
      console.error("Failed to add round:", e);
      setError("Failed to add a round. Please try again.");
    } finally {
      setIsMutatingSets(false);
    }
  };

  const handleDeleteLastSet = async (exerciseId: string) => {
    if (isMutatingSets) return;
    setIsMutatingSets(true);
    setError(null);
    try {
      const currentSets = getEffectiveSetsCount(exerciseId);
      if (currentSets <= 1) return;

      // Ensure we have the trackedSetId for the last set
      let lastSetId = exerciseSetData[exerciseId]?.find((s) => s.set === currentSets)?.trackedSetId;
      if (!lastSetId) {
        const refreshed = await refreshExerciseTrackedSets(exerciseId);
        lastSetId = refreshed.find((s) => s.set === currentSets)?.trackedSetId;
      }
      if (!lastSetId) {
        throw new Error("missing_tracked_set_id_for_last_set");
      }

      await deleteTrackedSet(lastSetId);
      await refreshExerciseTrackedSets(exerciseId);
    } catch (e) {
      console.error("Failed to delete last set:", e);
      setError("Failed to delete the last set. Please try again.");
    } finally {
      setIsMutatingSets(false);
    }
  };

  const scrollToProgressBar = () => {
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      if (progressBarRef.current) {
        progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const navigateToNextStep = () => {
    if (safeActiveStepIndex < totalSteps - 1) {
      setActiveStepIndex(safeActiveStepIndex + 1);
      // Scroll to progress bar after state update and DOM render
      scrollToProgressBar();
    }
  };

  const navigateToPrevStep = () => {
    if (safeActiveStepIndex > 0) {
      setActiveStepIndex(safeActiveStepIndex - 1);
      // Scroll to progress bar after state update and DOM render
      scrollToProgressBar();
    }
  };

  const isExerciseCompleted = (exerciseId: string) => {
    const stepIndexes = stepIndexesByExerciseId.get(exerciseId) ?? [];
    return completedExercises.includes(exerciseId) || (stepIndexes.length > 0 && stepIndexes.every((index) => index < safeActiveStepIndex));
  };
  
  // Ensure we have set data for the current exercise
  // Make sure the number of sets matches exercise.sets
  let normalizedExerciseSetData = exerciseSetData[activeExercise?.id] || [];
  const effectiveSets = activeExercise ? (exerciseSetData[activeExercise.id]?.length ?? activeExercise.sets) : 0;
  if (activeExercise && effectiveSets > 0 && normalizedExerciseSetData.length !== effectiveSets) {
    // Adjust to match effective set count (state is source of truth after add/delete)
    if (normalizedExerciseSetData.length < effectiveSets) {
      // Add missing sets - these should ideally have trackedSetIds from the transform function
      // If they don't, the API will auto-generate them when we try to save
      const existingSetNumbers = new Set(normalizedExerciseSetData.map(s => s.set));
      for (let i = 1; i <= effectiveSets; i++) {
        if (!existingSetNumbers.has(i)) {
          normalizedExerciseSetData.push({
            set: i,
            weight: undefined,
            reps: undefined,
            duration: undefined,
            trackedSetId: undefined, // Will be created by API on first save if missing
          });
        }
      }
      normalizedExerciseSetData.sort((a, b) => a.set - b.set);
    } else {
      // Remove extra sets
      normalizedExerciseSetData = normalizedExerciseSetData.slice(0, effectiveSets);
    }
  }
  const currentExerciseSetData = activeStep?.isGrouped
    ? normalizedExerciseSetData.filter((setData) => (
      setData.set >= activeStep.displayStartSet &&
      setData.set < activeStep.displayStartSet + activeStep.displaySetCount
    ))
    : normalizedExerciseSetData;

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Workout</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Link to="/start-workout">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Workouts
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !isWorkoutActive || !activeWorkout || !activeExercise) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg font-medium">Loading workout...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your workout details.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen gradient-dark pb-20">
      {/* Header */}
      <div className="bg-dark-card border-b border-primary/20">
        <div className="max-w-md mx-auto px-4 py-4 text-white">
          <div className="flex items-center justify-between">
            <Link to="/start-workout">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-bold">{activeWorkout.title}</h1>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                console.log("End workout button clicked in header");
                endWorkout(elapsedSeconds);
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-32">
        {/* Workout Progress */}
        <div ref={progressBarRef} className="mb-4">
          <div className="flex justify-between text-sm text-gray-light mb-1">
            <span>Progress: {currentStepNumber}/{totalSteps} steps</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        {/* Workout Controls */}
        <div className="flex flex-col gap-3 mb-6 bg-dark-card p-4 rounded-lg shadow-sm border border-primary/20">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline"
              size="sm"
              onClick={pauseResumeWorkout}
              className="text-white"
            >
              {isPaused ? <Play className="h-4 w-4 mr-1" /> : <Pause className="h-4 w-4 mr-1" />}
              {isPaused ? "Resume" : "Pause"}
            </Button>
            
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm font-medium text-white">
                {isGroupedExercise ? `Step ${currentStepNumber} of ${totalSteps}` : `Exercise ${currentStepNumber} of ${totalSteps}`}
              </span>
              {/* Timer directly under Exercise count */}
              <div className="flex items-center justify-center gap-2 px-4 py-2 bg-dark-slate rounded-lg border border-primary/20">
                <Clock className="h-5 w-5 text-primary" />
                <span className="text-lg font-bold text-white tabular-nums">{formatTimer(elapsedSeconds)}</span>
              </div>
            </div>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                console.log("End workout button clicked in controls");
                endWorkout(elapsedSeconds);
              }}
              className="text-red-600 border-red-200"
            >
              End
            </Button>
          </div>
        </div>
        
        {/* Current Exercise Detail */}
        <Card className="mb-6 overflow-hidden bg-dark-card border border-primary/20">
          <CardHeader className="gradient-primary p-4">
            {isGroupedExercise && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge className="bg-black/80 text-primary">
                  {activeGrouping?.label}
                </Badge>
                <Badge variant="outline" className="border-black/20 bg-black/10 text-black">
                  Exercise {activeExerciseIndexInGroup + 1} of {activeGrouping?.size}
                </Badge>
                <Badge variant="outline" className="border-black/20 bg-black/10 text-black">
                  Round {activeStep.roundIndex + 1} of {groupRounds}
                </Badge>
                {groupRestSeconds > 0 && (
                  <Badge variant="outline" className="border-black/20 bg-black/10 text-black">
                    Rest after round {formatRestTime(groupRestSeconds)}
                  </Badge>
                )}
              </div>
            )}
            <h2 className="text-black text-lg font-bold">{activeExercise.name}</h2>
            <div className="flex flex-col space-y-2">
              {hasEquipment && (
                <div className="flex items-center text-black/80">
                  <Dumbbell className="h-4 w-4 mr-1" />
                  <span className="text-sm">{equipmentNames.join(", ")}</span>
                </div>
              )}
              <Badge variant="outline" className="bg-transparent text-primary border-primary/30 w-fit">
                {activeExercise.muscleGroup}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4 text-white">
            {isGroupedExercise && activeExerciseGroup && (
              <div className="mb-4 rounded-lg border border-primary/20 bg-dark-slate p-3">
                <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide text-gray-light">
                  <span>{activeGrouping?.label} flow</span>
                  <span>Group {activeExerciseGroupIndex + 1} of {exerciseGroups.length}</span>
                </div>
                <div className="grid gap-2">
                  {activeExerciseGroup.exercises.map((exercise, index) => {
                    const isActive = exercise.id === activeExercise.id;
                    const stepIndex = getStepIndexForExercise(exercise.id, {
                      groupId: activeExerciseGroup.id,
                      roundIndex: activeStep.roundIndex,
                    });
                    const isComplete = stepIndex > -1 && stepIndex < safeActiveStepIndex;
                    return (
                      <button
                        key={exercise.id}
                        type="button"
                        onClick={() => stepIndex > -1 && setActiveStepIndex(stepIndex)}
                        className={cn(
                          "flex items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors",
                          isActive ? "border-primary/50 bg-primary/10" : "border-white/10 bg-black/20 hover:border-primary/30",
                          isComplete ? "opacity-70" : ""
                        )}
                      >
                        <span className="text-sm font-medium text-white">{exercise.name}</span>
                        <span className="text-xs text-gray-light">
                          {isComplete ? "Done" : isActive ? "Current" : `Exercise ${index + 1}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {isLastExerciseInGroup && groupRestSeconds > 0 && (
                  <p className="mt-3 text-xs text-gray-light">
                    Finish this exercise to take {formatRestTime(groupRestSeconds)} before the next round or section.
                  </p>
                )}
              </div>
            )}
            <div className="mb-4">
              <ExerciseMedia exercise={activeExercise} />
            </div>
            
            <div className={cn(
              "grid gap-3 mb-4",
              isGroupedExercise
                ? "grid-cols-2 md:grid-cols-4"
                : activeExercise.reps && activeExercise.restTime
                  ? "grid-cols-3"
                  : (activeExercise.reps || activeExercise.restTime)
                    ? "grid-cols-2"
                    : "grid-cols-1"
            )}>
              <div className="bg-dark-slate p-3 rounded-lg text-center">
                <p className="text-xs text-gray-light">{isGroupedExercise ? "Rounds" : "Sets"}</p>
                <p className="font-bold">{isGroupedExercise ? groupRounds : effectiveSets}</p>
              </div>
              {isGroupedExercise && (
                <div className="bg-dark-slate p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-light">Sets This Visit</p>
                  <p className="font-bold">{currentExerciseSetData.length}</p>
                </div>
              )}
              {activeExercise.reps && (
                <div className="bg-dark-slate p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-light">Reps</p>
                  <p className="font-bold">{activeExercise.reps}</p>
                </div>
              )}
              {activeExercise.restTime && (
                <div className="bg-dark-slate p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-light">Rest</p>
                  <p className="font-bold">{activeExercise.restTime} sec</p>
                </div>
              )}
              {isGroupedExercise && groupRestSeconds > 0 && (
                <div className="bg-dark-slate p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-light">Rest After Round</p>
                  <p className="font-bold">{formatRestTime(groupRestSeconds)}</p>
                </div>
              )}
            </div>
            
            {/* Exercise Information Accordion */}
            <Accordion type="single" collapsible className="mb-6">
              {hasExerciseInstructions && (
                <AccordionItem value="instructions" className="border-b-0">
                  <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg hover:no-underline">
                    Exercise Instructions
                  </AccordionTrigger>
                  <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg text-sm text-gray-light">
                    {activeExercise.instructions}
                  </AccordionContent>
                </AccordionItem>
              )}

              {hasEquipment && (
                <AccordionItem value="equipment" className={cn("border-b-0", hasExerciseInstructions ? "mt-2" : "")}>
                  <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg hover:no-underline">
                    Equipment Information
                  </AccordionTrigger>
                  <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg">
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {equipmentNames.map((equipmentName) => (
                          <Badge
                            key={equipmentName}
                            variant="outline"
                            className="border-primary/30 bg-dark-card text-xs text-white"
                          >
                            {equipmentName}
                          </Badge>
                        ))}
                      </div>

                      {hasEquipmentInstructions && (
                        <div className="space-y-2 text-sm text-gray-light">
                          {equipmentDetails.map((detail) => (
                            <div key={`${detail.name}-${detail.instructions}`}>
                              <p className="mb-1 font-medium text-white">{detail.name}:</p>
                              <p>{detail.instructions}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
            
            {/* Set Progress - Updated to handle missing set data properly */}
            <div className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-light">
                    {isGroupedExercise ? "Track this round for the current exercise" : "Track your sets"}
                  </h3>
                  {isGroupedExercise && (
                    <p className="mt-1 text-xs text-gray-light">
                      {activeGrouping?.setsPerRound && activeGrouping.setsPerRound > 1
                        ? `This exercise shows ${activeGrouping.setsPerRound} sets each round of the ${activeGrouping.label.toLowerCase()}.`
                        : `This exercise contributes one tracked set each round of the ${activeGrouping?.label?.toLowerCase()}.`}
                      {" "}Use Add round to repeat all exercises in this {activeGrouping?.label?.toLowerCase()}.
                    </p>
                  )}
                </div>
                {showSetMutationControls && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddSet(activeExercise.id)}
                    disabled={isMutatingSets}
                    className="h-9"
                  >
                    <Plus className="h-4 w-4" />
                    Add set
                  </Button>
                )}
                {isGroupedExercise && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddGroupRound}
                    disabled={isMutatingSets}
                    className="h-9"
                  >
                    <Plus className="h-4 w-4" />
                    Add round
                  </Button>
                )}
              </div>
              {currentExerciseSetData && currentExerciseSetData.length > 0 ? (
                <div className="space-y-3">
                  {currentExerciseSetData.map((setData, index) => {
                    const trackedSetId = setData.trackedSetId || null;
                    const restSeconds = activeExercise.restTime ? (typeof activeExercise.restTime === 'string' ? parseInt(activeExercise.restTime) : activeExercise.restTime) : null;
                    const isLastSet = index === currentExerciseSetData.length - 1;
                    return (
                      <SetTrackingRow 
                        key={`${activeExercise.id}-set-${setData.set}`}
                        setData={setData} 
                        exerciseId={activeExercise.id}
                        exercise={activeExercise}
                        trackedSetId={trackedSetId}
                        onValueChange={handleSetValueChange}
                        canDelete={showSetMutationControls && isLastSet && effectiveSets > 1}
                        onDelete={() => handleDeleteLastSet(activeExercise.id)}
                        deleteDisabled={isMutatingSets}
                        showRestAfter={!isLastSet}
                        restSeconds={restSeconds}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 bg-dark-slate rounded-lg text-center text-gray-light">
                  No set tracking data available for this exercise.
                </div>
              )}
            </div>
            
            {/* Navigation */}
            <div className="space-y-3">
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={safeActiveStepIndex === 0}
                  onClick={navigateToPrevStep}
                >
                  <ArrowLeft className="mr-1 h-5 w-5" /> Previous
                </Button>
                <Button
                  className="flex-1 bg-primary text-black hover:bg-primary/90"
                  disabled={safeActiveStepIndex === totalSteps - 1}
                  onClick={navigateToNextStep}
                >
                  Next <ArrowRight className="ml-1 h-5 w-5" />
                </Button>
              </div>
              
              {/* End Workout Button - Only show on last exercise */}
              {safeActiveStepIndex === totalSteps - 1 && (
                <Button
                  variant="outline"
                  className="w-full bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300"
                  onClick={() => {
                    console.log("End workout button clicked in navigation");
                    endWorkout(elapsedSeconds);
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  End Workout
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Exercise List (Mini) */}
        <div className="bg-dark-card rounded-lg p-4 shadow-sm border border-primary/20">
          <h3 className="font-medium mb-3">Workout Progress:</h3>
          <div className="space-y-2">
            {exerciseGroups.map((group, groupIndex) => (
              <div
                key={group.id}
                className={cn(
                  "rounded-lg border p-2",
                  group.label === "Single" ? "border-primary/20 bg-dark-slate" : "border-primary/30 bg-black/20"
                )}
              >
                {group.label !== "Single" && (
                  <div className="mb-2 flex items-center justify-between px-1">
                    <div>
                      <p className="text-sm font-semibold text-white">{group.label}</p>
                      <p className="text-xs text-gray-light">
                        {group.exercises.length} exercises, {group.rounds} rounds
                      </p>
                    </div>
                    {group.restSeconds > 0 && (
                      <Badge variant="outline" className="border-primary/30 text-primary">
                        Rest {formatRestTime(group.restSeconds)}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  {group.exercises.map((exercise) => {
                    const index = getStepIndexForExercise(exercise.id, { groupId: group.id });
                    return (
                      <div
                        key={exercise.id}
                        className={cn(
                          "flex items-center justify-between rounded-lg border p-2 cursor-pointer",
                          index === safeActiveStepIndex ? "bg-primary/10 border-primary/40" : "bg-dark-slate border-primary/20",
                          isExerciseCompleted(exercise.id) ? "border-l-4 border-primary" : ""
                        )}
                        onClick={() => index > -1 && setActiveStepIndex(index)}
                      >
                        <div className="flex items-center">
                          <div className={cn(
                            "rounded-full w-6 h-6 flex items-center justify-center mr-2",
                            isExerciseCompleted(exercise.id)
                              ? "bg-primary/20 text-primary"
                              : index === safeActiveStepIndex
                                ? "bg-primary/30 text-primary"
                                : "bg-dark-card text-gray-light"
                          )}>
                            {isExerciseCompleted(exercise.id) ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <span className="text-xs font-medium">{Math.max(index + 1, 1)}</span>
                            )}
                          </div>
                          <div>
                            <p className={cn(
                              "font-medium text-sm",
                              isExerciseCompleted(exercise.id) ? "line-through text-gray-light" : ""
                            )}>
                              {exercise.name}
                            </p>
                            {group.label !== "Single" && (
                              <p className="text-xs text-gray-light">
                                Exercise {group.exercises.findIndex((item) => item.id === exercise.id) + 1} of {group.exercises.length} in group {groupIndex + 1}
                                {index > -1 && sessionSteps[index]
                                  ? ` • Round ${sessionSteps[index].roundIndex + 1} of ${sessionSteps[index].roundCount}`
                                  : ""}
                              </p>
                            )}
                          </div>
                        </div>
                        {index === safeActiveStepIndex && (
                          <div className="bg-primary/20 rounded-full p-1">
                            <Play className="h-3 w-3 text-primary" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutSession;
